import React from 'react';
import { useSelector } from 'react-redux';

import { RootState } from '../../../store';
import './DrillBreadcrumb.scss';

export interface DrillBreadcrumbProps {
  /** Currently selected district name (null = state level). */
  district?: string | null;
  /** Currently selected block name. */
  block?: string | null;
  /** Currently selected school name. Omit on tables that stop at block. */
  school?: string | null;
  /** Root crumb label. Defaults to 'Tamil Nadu'. */
  rootLabel?: string;
  /**
   * Step up one level. Defaults to clearing the deepest selected level via
   * the handlers below; pass this when a page keeps extra local drill state
   * that has to be unwound too.
   */
  onBack?: () => void;
  /**
   * Jump to a level by clearing it. Each crumb clears the level *below* it,
   * which is what moves the view up: clicking the district crumb clears the
   * block (showing that district's blocks again), and clicking the root
   * crumb clears the district (showing all districts).
   *
   * These are the same handlers the header dropdowns use, so the dropdowns
   * stay in sync when the user navigates by breadcrumb.
   */
  onDistrictChange?: (value: string) => void;
  onBlockChange?: (value: string) => void;
  onSchoolChange?: (value: string) => void;
  /** Extra class names on the wrapper. */
  className?: string;
}

/**
 * Back button + Tamil Nadu › District › Block › School trail shown above a
 * drill-down table.
 *
 * Renders nothing at state level (no district selected) — there is nowhere to
 * go back to. The deepest selected level is plain text; every level above it
 * is a button that navigates there.
 */
const DrillBreadcrumb: React.FC<DrillBreadcrumbProps> = ({
  district,
  block,
  school,
  rootLabel = 'Tamil Nadu',
  onBack,
  onDistrictChange,
  onBlockChange,
  onSchoolChange,
  className = '',
}) => {
  // District/block-level users are pinned to their own district/block (the
  // header dropdowns are frozen for them — see Dashboard.tsx). This
  // breadcrumb is used on ~20 pages, so rather than threading a locked flag
  // through every one of their prop chains, it reads the session directly:
  // one source of truth, applied everywhere automatically.
  const userRoleLevel = useSelector(
    (state: RootState) => state.session.user_role_level
  );
  const districtLocked =
    userRoleLevel === 'district' || userRoleLevel === 'block';
  const blockLocked = userRoleLevel === 'block';

  if (!district) return null;

  // Back steps up exactly one level: school → block → district → state.
  // Clearing school is always within bounds; clearing block escapes a
  // block-locked user's fixed block; clearing district escapes a
  // district-locked (or block-locked) user's fixed district.
  const backEscapesLock = school ? false : block ? blockLocked : districtLocked;
  const goBack =
    onBack ??
    (() => {
      if (school) onSchoolChange?.('');
      else if (block) onBlockChange?.('');
      else onDistrictChange?.('');
    });

  return (
    <div className={`drill-crumbs ${className}`.trim()}>
      {!backEscapesLock && (
        <button type="button" className="drill-crumbs__back" onClick={goBack}>
          <i className="pi pi-arrow-left" aria-hidden="true" /> Back
        </button>
      )}

      <nav className="drill-crumbs__trail" aria-label="Drill-down breadcrumb">
        {/* Root — navigable only for unlocked (state-level) users; a
            district/block-locked user can never go back to the state level. */}
        {districtLocked ? (
          <span className="drill-crumbs__crumb is-current">{rootLabel}</span>
        ) : (
          <button
            type="button"
            className="drill-crumbs__crumb"
            onClick={() => onDistrictChange?.('')}
          >
            {rootLabel}
          </button>
        )}

        <i className="pi pi-angle-right" aria-hidden="true" />

        {/* District — navigable when something deeper is selected, UNLESS
            the user is block-locked (clicking this only ever clears block,
            which a block-locked user must not be able to do; a plain
            district-locked user may still browse other blocks in their own
            district, so this stays enabled for them). */}
        {block && !blockLocked ? (
          <button
            type="button"
            className="drill-crumbs__crumb"
            onClick={() => onBlockChange?.('')}
          >
            {district}
          </button>
        ) : (
          <span
            className="drill-crumbs__crumb is-current"
            aria-current={!block ? 'page' : undefined}
          >
            {district}
          </span>
        )}

        {block && (
          <>
            <i className="pi pi-angle-right" aria-hidden="true" />
            {school ? (
              <button
                type="button"
                className="drill-crumbs__crumb"
                onClick={() => onSchoolChange?.('')}
              >
                {block}
              </button>
            ) : (
              <span
                className="drill-crumbs__crumb is-current"
                aria-current="page"
              >
                {block}
              </span>
            )}
          </>
        )}

        {school && (
          <>
            <i className="pi pi-angle-right" aria-hidden="true" />
            <span
              className="drill-crumbs__crumb is-current"
              aria-current="page"
            >
              {school}
            </span>
          </>
        )}
      </nav>
    </div>
  );
};

export default DrillBreadcrumb;
